/**
 * Card — экран выпущенной карты (Flow step="card"): само «лицо» карты, кнопки «Пополнить» /
 * «Вывести», история операций и свёрнутая строка с запрещёнными операциями.
 *
 * История — лента всех движений: выпуск, пополнение со счёта, возврат на счёт, конвертация с
 * выводом криптовалюты (одной операцией) и вывод за пределы сервиса. У операций с блокчейном
 * есть статус («В обработке» / «Не выполнено»), остальные завершаются сразу.
 * Пропсы: card, onTopUp, onWithdraw, onBack.
 */
import { useEffect, useState } from "react";
import { useBackButton } from "@/telegram/useMainButton";
import { Screen } from "@/components/Screen";
import { Spinner } from "@/components/Spinner";
import { cardsApi, type CardOperation, type IssuedCard } from "@/lib/api";
import { formatRelative, formatRub } from "@/lib/format";
import { CardFace } from "./CardFace";
import { RestrictionsCollapsible } from "./restrictions";

type Props = {
  card: IssuedCard;
  onTopUp: () => void;
  onWithdraw: () => void;
  onBack: () => void;
};

const KIND_LABEL: Record<CardOperation["kind"], string> = {
  issue: "Выпуск карты",
  topup: "Пополнение со счёта",
  refund: "Возврат на счёт",
  crypto_out: "Вывод в криптовалюту",
  withdrawal: "Вывод с карты",
};

const KIND_ICON: Record<CardOperation["kind"], string> = {
  issue: "💳",
  topup: "⬆️",
  refund: "⬇️",
  crypto_out: "🪙",
  withdrawal: "🏦",
};

function StatusTag({ status }: { status: CardOperation["status"] }) {
  if (status === "completed") return null;
  const warn = status === "pending";
  return (
    <span
      className={`ml-2 shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-medium ${
        warn ? "bg-amber-400/15 text-amber-300" : "bg-rose-500/15 text-rose-300"
      }`}
    >
      {warn ? "В обработке" : "Не выполнено"}
    </span>
  );
}

function OperationRow({ op }: { op: CardOperation }) {
  const incoming = op.amount_usd > 0;
  // вторая сторона обмена: рубли счёта, USDT или ничего (выпуск)
  const counter =
    op.counter_asset === "RUB"
      ? formatRub(op.counter_amount ?? 0)
      : op.counter_asset === "USDT"
        ? `${(op.counter_amount ?? 0).toFixed(2)} USDT`
        : null;

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white/[0.06] text-[16px]">
        {KIND_ICON[op.kind]}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center">
          <span className="truncate text-[14px] font-medium">{KIND_LABEL[op.kind]}</span>
          <StatusTag status={op.status} />
        </div>
        <div className="mt-0.5 text-[12px] text-white/40">
          {op.created_at ? formatRelative(Date.parse(op.created_at)) : ""}
          {op.rate ? ` · курс ${op.rate.toFixed(2)} ₽` : ""}
          {op.fee_amount ? ` · комиссия ${op.fee_asset === "RUB" ? formatRub(op.fee_amount) : `${op.fee_amount.toFixed(2)} ${op.fee_asset ?? ""}`}` : ""}
        </div>
      </div>
      <div className="shrink-0 text-right">
        {op.amount_usd !== 0 && (
          <div className={`text-[14px] font-semibold tabular-nums ${incoming ? "text-brand" : "text-white"}`}>
            {incoming ? "+" : "−"}${Math.abs(op.amount_usd).toFixed(2)}
          </div>
        )}
        {counter && <div className="mt-0.5 text-[11px] tabular-nums text-white/40">{counter}</div>}
      </div>
    </div>
  );
}

export function Card({ card, onTopUp, onWithdraw, onBack }: Props) {
  const [ops, setOps] = useState<CardOperation[] | null>(null);

  useEffect(() => {
    cardsApi
      .operations()
      .then((r) => setOps(r.operations))
      .catch(() => setOps([]));
  }, []);

  useBackButton(onBack);

  return (
    <Screen title={card.design?.name ?? "Карта"} subtitle={`Виртуальная карта •• ${card.last4}`}>
      <div className="mx-auto w-full max-w-[22rem]">
        <CardFace card={card} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2.5">
        <button
          onClick={onTopUp}
          className="flex items-center justify-center gap-1.5 rounded-2xl bg-brand px-4 py-3.5 text-[15px] font-semibold text-surface-base shadow-lg shadow-brand/25 active:brightness-95"
        >
          <span className="text-[17px] leading-none">↓</span> Пополнить
        </button>
        <button
          onClick={onWithdraw}
          className="flex items-center justify-center gap-1.5 rounded-2xl border border-white/[0.08] bg-white/[0.04] px-4 py-3.5 text-[15px] font-semibold text-white backdrop-blur-xl active:opacity-80"
        >
          <span className="text-[17px] leading-none">↑</span> Вывести
        </button>
      </div>

      <div className="mt-4">
        <RestrictionsCollapsible />
      </div>

      <div className="mt-5">
        <div className="mb-2 px-1 text-[10px] font-medium uppercase tracking-[0.18em] text-white/40">
          История операций
        </div>
        {ops === null ? (
          <div className="flex justify-center py-8">
            <Spinner className="h-6 w-6" />
          </div>
        ) : ops.length === 0 ? (
          <div className="rounded-2xl border border-white/[0.06] bg-surface-card px-4 py-8 text-center text-[13px] text-white/45">
            Операций пока нет
          </div>
        ) : (
          <div className="divide-y divide-white/[0.05] overflow-hidden rounded-2xl border border-white/[0.06] bg-surface-card">
            {ops.map((op) => (
              <OperationRow key={op.id} op={op} />
            ))}
          </div>
        )}
      </div>
    </Screen>
  );
}
